import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

interface Spec {
  label: string;
  value: string;
}

interface Props {
  specs: Spec[];
  features: string[];
  className?: string;
}

export const Rem7HydraSpecPDFButton = ({ specs, features, className }: Props) => {
  const generate = () => {
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;

      // Header band
      doc.setFillColor(13, 13, 13);
      doc.rect(0, 0, pageWidth, 32, "F");
      doc.setTextColor(201, 168, 76);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("AUREUM VII TRADING", margin, 13);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(15);
      doc.text("REM7 Hydra · Whirlpool Specifications", margin, 22);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(200, 200, 200);
      doc.text("SKU: REM7-HYDRA-1825 · CE / SGS / cUPC certified", margin, 28);

      let y = 44;

      // Quick features
      doc.setTextColor(45, 45, 45);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("QUICK FEATURES", margin, y);
      doc.setDrawColor(201, 168, 76);
      doc.setLineWidth(0.4);
      doc.line(margin, y + 2, margin + 30, y + 2);
      y += 8;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      features.forEach((f) => {
        const lines = doc.splitTextToSize(`•  ${f}`, contentWidth);
        doc.text(lines, margin, y);
        y += lines.length * 5 + 1;
      });

      y += 6;

      // Specs table
      doc.setTextColor(45, 45, 45);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("FULL TECHNICAL SPECIFICATIONS", margin, y);
      doc.line(margin, y + 2, margin + 60, y + 2);
      y += 8;

      doc.setFontSize(9);
      const labelW = 65;
      specs.forEach((s, i) => {
        if (y > pageHeight - 25) {
          doc.addPage();
          y = margin;
        }
        if (i % 2 === 0) {
          doc.setFillColor(248, 248, 246);
          doc.rect(margin, y - 4, contentWidth, 8, "F");
        }
        doc.setFont("helvetica", "bold");
        doc.setTextColor(80, 80, 80);
        doc.text(s.label, margin + 2, y + 1);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
        const valLines = doc.splitTextToSize(s.value, contentWidth - labelW - 4);
        doc.text(valLines, margin + labelW, y + 1);
        y += Math.max(8, valLines.length * 5);
      });

      // Contact footer
      if (y > pageHeight - 40) {
        doc.addPage();
        y = margin;
      } else {
        y += 8;
      }
      doc.setFillColor(13, 13, 13);
      doc.rect(margin, y, contentWidth, 26, "F");
      doc.setTextColor(201, 168, 76);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("PROJECT ENQUIRIES", margin + 4, y + 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(235, 235, 235);
      doc.text("Email: info@aureum7trading.com", margin + 4, y + 15);
      doc.text("WhatsApp: +34 642 278 939", margin + 4, y + 21);
      doc.text("Web: www.aureum7trading.com/en/bath-sanitary/rem7-whirlpool", margin + 95, y + 15);
      doc.text("MOQ from 5 · Bulk pricing 20+ · 40ft ≈ 28 units", margin + 95, y + 21);

      // Page footer
      const fy = pageHeight - 8;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `REM7 Hydra Whirlpool Specifications · © ${new Date().getFullYear()} Aureum VII Trading`,
        pageWidth / 2,
        fy,
        { align: "center" }
      );

      doc.save("REM7_Hydra_Whirlpool_Specifications.pdf");
      toast.success("REM7 Hydra specifications downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <Button
      onClick={generate}
      variant="outline"
      className={`gap-2 border-amber-500/60 text-neutral-900 hover:bg-amber-50 ${className ?? ""}`}
    >
      <FileText className="h-4 w-4" />
      <span>Download Full Specifications (PDF)</span>
      <Download className="h-4 w-4" />
    </Button>
  );
};

export default Rem7HydraSpecPDFButton;
